import type { Bulle, Feuille, FicheEnBase, HabitEnBase, Maillon } from '../partage/api/types';
import { laMere } from './ficheEnMots';

/**
 * Ouvrir la fiche d'une bulle par son identifiant, une seule façon (chantier nettoyage-feuille,
 * ticket 01). Avant, trois façons de trouver le chemin d'une bulle (`BulleLointaine` de la
 * Hiérarchie et de la Liste, `loinDe` de la Carte, `ouvertureDepuisIci` du téléphone) et trois
 * « lire la feuille de la bulle puis aller chez sa mère » (« Projets », l'Agenda, la fiche).
 *
 * Les règles, sans React : la bulle **déjà lue** et son chemin (décision du cadrage : aucun
 * chargement de plus, on prend d'abord ce qui est lu), où vit sa mère, et si sa fiche s'ouvre
 * sur place ou ailleurs. Le crochet qui s'en sert : `fiche/useAllerALaFiche.ts`.
 */

export interface FicheTrouvee {
  /** Lue sur une feuille, elle porte le type et le papier de sa fiche (chantier types-de-fiche). */
  bulle: Bulle & Partial<FicheEnBase> & Partial<HabitEnBase>;
  /** De la racine à la bulle comprise. */
  chemin: Maillon[];
}

/**
 * Où aller quand la fiche ne s'ouvre pas sur place : la feuille de sa mère, sa fiche par-dessus
 * (on la voit parmi ses sœurs), ou sa propre feuille, sa fiche sur le centre.
 */
export type Arrivee = 'chez-elle' | 'chez-sa-mere';

/** Ce que ces règles lisent d'une feuille ; la carte, pour les bulles qu'elle dessine plus bas. */
export type FeuilleLue = Pick<Feuille, 'bulle' | 'chemin' | 'enfants'> & {
  carte?: Pick<Feuille['carte'], 'dessous'>;
};

/**
 * La bulle telle qu'une feuille déjà lue la porte, et son chemin. Les feuilles se lisent dans
 * l'ordre donné, chacune son centre puis ses filles : la première qui la porte l'emporte.
 */
export function laBulleLue(id: string, feuilles: readonly (FeuilleLue | undefined)[]): FicheTrouvee | null {
  for (const f of feuilles) {
    if (!f) continue;
    if (f.bulle.id === id) return { bulle: f.bulle, chemin: f.chemin };
    const fille = laFille(id, f);
    if (fille) return fille;
  }
  return null;
}

/**
 * La fiche ouverte sur une feuille : la feuille elle-même (son centre, ses filles), puis les
 * **filles** des autres feuilles lues, dans l'ordre (celle de la mère du centre pour ses sœurs,
 * celle de la mère d'une bulle dépliée plus bas). Le centre de ces autres feuilles ne compte
 * pas : la fiche de la mère du centre, laissée ouverte en descendant, ne se montre pas.
 */
export function laFicheOuverte(
  id: string,
  feuille: FeuilleLue,
  autres: readonly (FeuilleLue | undefined)[],
  /**
   * La feuille de la bulle elle-même, lue quand on ouvre sur place la fiche d'une bulle d'ailleurs
   * (l'étiquette d'un lien, chantier liens-entre-bulles, ticket 05) : son centre la porte, en
   * dernier recours (un projet n'a pas de mère où la trouver).
   */
  sienne?: FeuilleLue
): FicheTrouvee | null {
  const ici = laBulleLue(id, [feuille]);
  if (ici) return ici;
  for (const f of autres) {
    const fille = f ? laFille(id, f) : null;
    if (fille) return fille;
  }
  return sienne && sienne.bulle.id === id ? { bulle: sienne.bulle, chemin: sienne.chemin } : null;
}

/** Une fille de la feuille, et son chemin : celui de la feuille, plus elle. */
function laFille(id: string, f: FeuilleLue): FicheTrouvee | null {
  const fille = f.enfants.find((e) => e.id === id);
  return fille ? { bulle: fille, chemin: [...f.chemin, { id: fille.id, titre: fille.titre }] } : null;
}

/**
 * La mère d'une bulle, d'après ce qui est lu : la feuille qui la porte en fille, son propre
 * parent si elle est le centre d'une feuille, ou la carte qui la dessine plus bas. `null` : un
 * projet, ou une bulle que rien de lu ne connaît.
 */
export function laMereLue(id: string, feuilles: readonly (FeuilleLue | undefined)[]): string | null {
  for (const f of feuilles) {
    if (!f) continue;
    if (f.enfants.some((e) => e.id === id)) return f.bulle.id;
    if (f.bulle.id === id) return f.bulle.parentId;
    const dessinee = f.carte?.dessous.find((e) => e.id === id);
    if (dessinee) return dessinee.parentId;
  }
  return null;
}

/**
 * Sur place quand la bulle est la feuille ouverte ou sous elle (son chemin commence par celui de
 * la feuille) : sa fiche trouve sa feuille et son fil. Sinon, ou hors d'une feuille
 * (`cheminDeLaFeuille` nul : « Projets », l'Agenda), l'adresse où aller selon `arrivee`. Un
 * chemin inconnu (pas de réseau, bulle partie) mène chez elle : la feuille dira ce qu'il en est.
 */
export function ouOuvrirLaFiche(
  id: string,
  cheminDeLaBulle: readonly Maillon[] | null,
  cheminDeLaFeuille: readonly Maillon[] | null,
  arrivee: Arrivee
): 'sur-place' | { feuille: string } {
  if (!cheminDeLaBulle) return { feuille: id };
  const dessous =
    cheminDeLaFeuille !== null && cheminDeLaFeuille.every((maillon, k) => cheminDeLaBulle[k]?.id === maillon.id);
  if (dessous) return 'sur-place';
  if (arrivee === 'chez-elle') return { feuille: id };
  return { feuille: laMere([...cheminDeLaBulle])?.id ?? id };
}
